'use client';

import React, { useState, FormEvent } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';

const packages = [
  { hours: 1, price: 45, label: '1 Hour', perHour: '$45/hr', popular: false },
  { hours: 5, price: 200, label: '5 Hours', perHour: '$40/hr', popular: false },
  { hours: 10, price: 380, label: '10 Hours', perHour: '$38/hr', popular: true },
  { hours: 25, price: 950, label: '25 Hours', perHour: '$38/hr', popular: false },
];

interface SelectedPackage {
  hours: number;
  price: number;
  label: string;
  perHour: string;
  popular: boolean;
}

interface CheckoutFormProps {
  clientSecret: string;
  selectedPackage: SelectedPackage;
  onSuccess: () => void;
  onCancel: () => void;
}

const CheckoutForm = ({ clientSecret, selectedPackage, onSuccess, onCancel }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const supabase = createClient();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        await fetch('/api/confirm-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId: paymentIntent.id }),
        });
      } catch (confirmErr) {
        console.error('Confirm payment backend error:', confirmErr);
      }
      onSuccess();
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div
          style={{
            background: 'rgba(244,196,48,0.08)',
            border: '1px solid rgba(244,196,48,0.25)',
            borderRadius: 8,
            padding: '0.85rem 1rem',
            marginBottom: '1.25rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem' }}>
            {selectedPackage.label} Package
          </span>
          <span style={{ color: '#f4c430', fontWeight: 700, fontSize: '1rem' }}>
            ${selectedPackage.price}
          </span>
        </div>
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {errorMessage && (
        <div
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8,
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            color: '#fca5a5',
            fontSize: '0.85rem',
          }}
        >
          {errorMessage}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          style={{
            flex: 1,
            padding: '0.85rem',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 8,
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.9rem',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            transition: 'border-color 0.2s',
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          style={{
            flex: 2,
            padding: '0.85rem',
            background: isProcessing ? 'rgba(244,196,48,0.5)' : '#f4c430',
            border: 'none',
            borderRadius: 8,
            color: '#0a1628',
            fontSize: '0.9rem',
            fontWeight: 700,
            cursor: !stripe || isProcessing ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
          }}
        >
          {isProcessing ? 'Processing...' : `Pay $${selectedPackage.price}`}
        </button>
      </div>
    </form>
  );
};

interface CustomerFormProps {
  selectedPackage: SelectedPackage;
  onProceed: (customerInfo: { firstName: string; lastName: string; email: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
  errorMessage: string;
}

const CustomerForm = ({ selectedPackage, onProceed, onCancel, isLoading, errorMessage }: CustomerFormProps) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    if (!firstName.trim() || !email.trim()) {
      setFormError('First name and email are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    onProceed({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem 1rem',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    color: '#fff',
    fontSize: '0.9rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.55)',
    marginBottom: '0.35rem',
    fontWeight: 500,
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          background: 'rgba(244,196,48,0.08)',
          border: '1px solid rgba(244,196,48,0.25)',
          borderRadius: 8,
          padding: '0.85rem 1rem',
          marginBottom: '1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem' }}>
          {selectedPackage.label} Package
        </span>
        <span style={{ color: '#f4c430', fontWeight: 700, fontSize: '1rem' }}>
          ${selectedPackage.price}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div>
          <label style={labelStyle}>First Name *</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Jane"
            style={inputStyle}
            required
          />
        </div>
        <div>
          <label style={labelStyle}>Last Name</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Smith"
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ marginBottom: '1.25rem' }}>
        <label style={labelStyle}>Email Address *</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jane@example.com"
          style={inputStyle}
          required
        />
      </div>

      {(formError || errorMessage) && (
        <div
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8,
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            color: '#fca5a5',
            fontSize: '0.85rem',
          }}
        >
          {formError || errorMessage}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          style={{
            flex: 1,
            padding: '0.85rem',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 8,
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.9rem',
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          style={{
            flex: 2,
            padding: '0.85rem',
            background: isLoading ? 'rgba(244,196,48,0.5)' : '#f4c430',
            border: 'none',
            borderRadius: 8,
            color: '#0a1628',
            fontSize: '0.9rem',
            fontWeight: 700,
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {isLoading ? 'Setting up...' : 'Continue to Payment'}
        </button>
      </div>
    </form>
  );
};

type CheckoutStep = 'idle' | 'customer-info' | 'payment';

const stripePromise = getStripe();

interface PricingPageProps {
  onOrderConfirmed?: (details: {
    packageLabel: string;
    packageHours: number;
    packagePrice: number;
    customerName: string;
    customerEmail: string;
  }) => void;
}

const PricingPage: React.FC<PricingPageProps> = ({ onOrderConfirmed }) => {
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>('idle');
  const [selectedPackage, setSelectedPackage] = useState<SelectedPackage | null>(null);
  const [clientSecret, setClientSecret] = useState('');
  const [isLoadingIntent, setIsLoadingIntent] = useState(false);
  const [intentError, setIntentError] = useState('');
  const [customerInfo, setCustomerInfo] = useState<{ firstName: string; lastName: string; email: string } | null>(null);

  const supabase = createClient();

  const handleSelectPackage = (pkg: SelectedPackage) => {
    setSelectedPackage(pkg);
    setIntentError('');
    setCheckoutStep('customer-info');
  };

  const handleCustomerProceed = async (info: { firstName: string; lastName: string; email: string }) => {
    if (!selectedPackage) return;
    setCustomerInfo(info);
    setIsLoadingIntent(true);
    setIntentError('');

    try {
      // Check if user is logged in
      const { data: { user } } = await supabase.auth.getUser();

      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentData: {
            amount: selectedPackage.price,
            currency: 'usd',
            packageHours: selectedPackage.hours,
            packageLabel: selectedPackage.label,
            description: `Stellar Scholar ${selectedPackage.label} Tutoring Package`,
          },
          customerInfo: {
            userId: user?.id ?? null,
            firstName: info.firstName,
            lastName: info.lastName,
            email: info.email,
            stripeCustomerId: null,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setIntentError(data?.error ?? 'Failed to set up payment.');
        setIsLoadingIntent(false);
        return;
      }

      if (!data?.clientSecret) {
        setIntentError('Failed to initialize payment. Please try again.');
        setIsLoadingIntent(false);
        return;
      }

      setClientSecret(data.clientSecret);
      setCheckoutStep('payment');
    } catch (err: any) {
      setIntentError(err?.message ?? 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoadingIntent(false);
    }
  };

  const handlePaymentSuccess = () => {
    if (onOrderConfirmed && selectedPackage && customerInfo) {
      onOrderConfirmed({
        packageLabel: selectedPackage.label,
        packageHours: selectedPackage.hours,
        packagePrice: selectedPackage.price,
        customerName: customerInfo.firstName + (customerInfo.lastName ? ` ${customerInfo.lastName}` : ''),
        customerEmail: customerInfo.email,
      });
    }
    handleClose();
  };

  const handleClose = () => {
    setCheckoutStep('idle');
    setSelectedPackage(null);
    setClientSecret('');
    setIntentError('');
    setCustomerInfo(null);
  };

  const elementsOptions: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#f4c430',
        colorBackground: '#0f1f3d',
        colorText: '#ffffff',
        colorDanger: '#ef4444',
        fontFamily: 'DM Sans, sans-serif',
        borderRadius: '8px',
      },
    },
  };

  const showModal = checkoutStep !== 'idle';

  return (
    <section
      style={{
        minHeight: '100vh',
        background: '#0a1628',
        padding: '4rem 1.5rem 5rem',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 700,
            color: '#f4c430',
            marginBottom: '0.75rem',
          }}
        >
          Tutoring Packages
        </h1>
        <p
          style={{
            fontSize: '1rem',
            color: 'rgba(255,255,255,0.6)',
            maxWidth: 480,
            margin: '0 auto',
            lineHeight: 1.6,
          }}
        >
          Flexible hour packages to fit your learning goals. All sessions are one-on-one and tailored to your needs.
        </p>
      </div>

      {/* Notice banner */}
      <div
        style={{
          maxWidth: 680,
          margin: '0 auto 2.5rem',
          background: 'rgba(244,196,48,0.08)',
          border: '1px solid rgba(244,196,48,0.25)',
          borderRadius: 10,
          padding: '0.9rem 1.25rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.6rem',
        }}
      >
        <span style={{ color: '#f4c430', fontSize: '1.1rem', flexShrink: 0, marginTop: 1 }}>ℹ</span>
        <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.55 }}>
          <strong style={{ color: '#f4c430' }}>Packages of hours must be purchased in advance.</strong>
        </p>
      </div>

      {/* Divider */}
      <div style={{ maxWidth: 900, margin: '0 auto 2rem', textAlign: 'center' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
          <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
            Regular Packages
          </span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
        </div>
      </div>

      {/* Pricing cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1.25rem',
          maxWidth: 900,
          margin: '0 auto',
        }}
      >
        {packages.map((pkg) => (
          <div
            key={pkg.hours}
            style={{
              background: pkg.popular
                ? 'linear-gradient(145deg, rgba(244,196,48,0.14) 0%, rgba(244,196,48,0.04) 100%)'
                : 'rgba(255,255,255,0.04)',
              border: pkg.popular
                ? '1.5px solid rgba(244,196,48,0.55)'
                : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 14,
              padding: '2rem 1.5rem',
              position: 'relative',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 32px rgba(0,0,0,0.35)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
              (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
            }}
          >
            {pkg.popular && (
              <div
                style={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#f4c430',
                  color: '#0a1628',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '0.25rem 0.85rem',
                  borderRadius: 20,
                  whiteSpace: 'nowrap',
                }}
              >
                Most Popular
              </div>
            )}

            <div
              style={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.45)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '0.5rem',
              }}
            >
              {pkg.label}
            </div>

            <div
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(2.2rem, 4vw, 2.8rem)',
                fontWeight: 700,
                color: '#f4c430',
                lineHeight: 1,
                marginBottom: '0.35rem',
              }}
            >
              ${pkg.price}
            </div>

            <div
              style={{
                fontSize: '0.82rem',
                color: 'rgba(255,255,255,0.4)',
                marginBottom: '1.5rem',
              }}
            >
              {pkg.perHour}
            </div>

            <div
              style={{
                borderTop: '1px solid rgba(255,255,255,0.08)',
                paddingTop: '1.25rem',
                fontSize: '0.88rem',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.55,
                marginBottom: '1.5rem',
              }}
            >
              {pkg.hours === 1 && 'Perfect for a single focused session to tackle a specific topic.'}
              {pkg.hours === 5 && 'Great for short-term exam prep or catching up on a subject.'}
              {pkg.hours === 10 && 'Ideal for ongoing support across a semester or term.'}
              {pkg.hours === 25 && 'Best value for long-term, consistent academic improvement.'}
            </div>

            <button
              onClick={() => handleSelectPackage(pkg)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: pkg.popular ? '#f4c430' : 'transparent',
                border: pkg.popular ? 'none' : '1px solid rgba(244,196,48,0.5)',
                borderRadius: 8,
                color: pkg.popular ? '#0a1628' : '#f4c430',
                fontSize: '0.88rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!pkg.popular) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(244,196,48,0.12)';
                }
              }}
              onMouseLeave={(e) => {
                if (!pkg.popular) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }
              }}
            >
              Purchase Package
            </button>
          </div>
        ))}
      </div>

      {/* Disclaimer */}
      <p
        style={{
          textAlign: 'center',
          marginTop: '2.5rem',
          fontSize: '0.78rem',
          color: 'rgba(255,255,255,0.3)',
          fontStyle: 'italic',
        }}
      >
        * Prices subject to change without notice.
      </p>

      {/* Checkout Modal Overlay */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && checkoutStep !== 'payment') handleClose();
          }}
        >
          <div
            style={{
              background: '#0f1f3d',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 16,
              padding: '2rem',
              width: '100%',
              maxWidth: 480,
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: '#f4c430',
                  fontSize: '1.3rem',
                  fontWeight: 700,
                  margin: 0,
                }}
              >
                Complete Your Purchase
              </h2>
              {checkoutStep !== 'payment' && (
                <button
                  onClick={handleClose}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '1.4rem',
                    cursor: 'pointer',
                    lineHeight: 1,
                    padding: '0.25rem',
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {/* Customer Info Step */}
            {checkoutStep === 'customer-info' && selectedPackage && (
              <CustomerForm
                selectedPackage={selectedPackage}
                onProceed={handleCustomerProceed}
                onCancel={handleClose}
                isLoading={isLoadingIntent}
                errorMessage={intentError}
              />
            )}

            {/* Payment Step */}
            {checkoutStep === 'payment' && clientSecret && selectedPackage && (
              <Elements stripe={stripePromise} options={elementsOptions}>
                <CheckoutForm
                  clientSecret={clientSecret}
                  selectedPackage={selectedPackage}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handleClose}
                />
              </Elements>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default PricingPage;
